import esriConfig from '@arcgis/core/config.js'
import Extent from '@arcgis/core/geometry/Extent.js'
import * as clipOperator from '@arcgis/core/geometry/operators/clipOperator.js'
import Polygon from '@arcgis/core/geometry/Polygon.js'
import SpatialReference from '@arcgis/core/geometry/SpatialReference.js'
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js'

import type { LayerRaw } from '@tsyl/karonte/lib/types/layers'

import { LAYER_ATTRIBUTIONS } from '@/constants/layers/attributions'
import type { EsriPolygon } from '@/services/areas'
import type { LayerInfo } from '@/types/LayerInfo'

// Esri Living Atlas thermal hotspot services. Each layer also points at its AGOL item
// (portalItem) so the map uses the symbology Esri publishes there as-is; the services
// themselves only expose a plain simple renderer.
const VIIRS_URL =
  'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Satellite_VIIRS_Thermal_Hotspots_and_Fire_Activity/FeatureServer/0'
const VIIRS_ITEM_ID = 'dece90af1a0242dcbf0ca36d30276aa3'
const MODIS_URL = 'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/MODIS_Thermal_v1/FeatureServer'
const MODIS_ITEM_ID = 'b8f4033069f141729ffb298b7418b653'

// Karonte cannot build a legend from the VIIRS renderer (its scale-based size visual
// variable has no stops), so the legend replicates Esri's FRP classes and symbols.
const viirsLegend = {
  title: {
    'en-US': 'Fire Radiative Power (MW)',
    'es-ES': 'Potencia radiativa del fuego (MW)',
    'pt-BR': 'Potência radiativa do fogo (MW)',
    'fr-FR': 'Puissance radiative du feu (MW)'
  },
  items: [
    {
      text: '> 750',
      image:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAaCAYAAACpSkzOAAAACXBIWXMAAA7EAAAOxAGVKw4bAAACS0lEQVRIic2WwW7aQBCG/yFOjIQEUaqKGCmhF57AiNxyR3LzGM4bgG9xboRjbnDvCzQHP0Oi+AEqTuaA5UaNAAkpOIHpISZdvAacUFX9j7Pj/XZmZ2es4B9J+W9Ak36jtKtmDIDNyKQDAAM+mF3aydh7n1vuVqAwsEyAOwBLawRoIDIwZyMMmmDQpVps2e8ChQ+WzrO5DbCx6aR/wHwRBk3jecpnueP2YCMofLB0zLlDRHpaiCBdUel+0m9U4zAJxLO5/UEIgNeU7qr0HUB1JSi6k8R0jR0PI8fD7NcTAGDnUxaFehn5ejnJXQ8Dy9wrtroSaNJvlITKetO0N8Tjtx+Y3AZL9pfHJ/zsDTFyPGh2DcpBNvYldwDIoKiEpZQlQeIH8e07HF2fSmtiVELq5GjGjrcWIsLGjpeQRjYRRbX2HY0cbyNE9F1xX1gCMVCi2OLi4tMoyZeBkgQiQEu9a0qJe4oR+XGYWing5TZdVGqlINkY8CUQmF0QLb2h3MlhqmJY+MZF4BsJxKAuAUugfL2MkeNh2huuhaiV/cRCYGTkiLKHVzdh0JScNbsG375bCVMr+9DsWgIEvtjNleVFuiTwhWhTDrI4uj6VWpBaKSBf/4LcSTHxAGLaJJBabNlh0DQQDTdR+dV9LUnuXrF9vhIEAM9TPlNUut+i3F1k6DxulEC54/Zg0m9Uo1b/3nHhvg6+q82DbwEDUA0Dy2SwnSI6F2ApXRtBC0WdtxsGlsk8/0pEGgD97ceEMAAyrjh3PgSKA9P4bgX6G/oNzdL1560TBEYAAAAASUVORK5CYII='
    },
    {
      text: '300-749',
      image:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAYAAAByUDbMAAAACXBIWXMAAA7EAAAOxAGVKw4bAAABm0lEQVQ4ja3UQWrbQBSA4f8pVoMoBUOrRb1JTiAb1MFbHSCmR+hOuUavYe1yBucA3RohkHWIbOSCSxfFNMzrQhrJjmslpZmVGM37eG8eb0a84hoN/ayreQw2RmWC6AN4RRiti3/C6sqkoqSojZsdBQWwbDemUCELozx7FqtLsxLlZiDhWJRlXZpFOMsXZ7G6NCuRHrI7ZV8+Yn8ooyuPy1l/XISb76VZvp/ltydYW1oH/fr2m593+5O03n25JEj8pnghrStTuJI7TJT0OQjo9h3YxvVYXc1jd9l2p2ehQ9BhQFxX8ziM1kWbmesa7MvHQegw+x60MdBiKpO29/+1Gkz0wVneWF4UeHROZdJjeAVYgKP2D0EXY6/fEFl1WBiti+3GFEAMTfuHmhAkPqPrDutGrEtDhUyUpTsMnIDeWAgSn7ef33AY5747LIzybLsxqcsuSHyCxG8mYKd4Y8G/vji6K1Xuw2l+igF8mOafno7UuTtU5X5wNgHCWb6oK5Ni+SrCx784zasxfcGr4UoGsld5z3p0XQBng5+uP8YHse4V3PouAAAAAElFTkSuQmCC'
    },
    {
      text: '100-299',
      image:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAsAAAALCAYAAACprHcmAAAACXBIWXMAAA7EAAAOxAGVKw4bAAABQklEQVQYlXWRPW7CUBCEZ997Nj+2JRIkp7BEk1C5JYLQcQCqSFwhscQZOIUlnJzGFQl9OAIUbhBxAMfgt2nAElEy1Wr0FTszCn8onfZdJ5glv311Pr5f732t5VgDLqHwd1E3YUZsBfPJBXwGiwMPdcZN1lwlgzxZodY26vnW8/tjCResBvqgh8VOe+kix355RM1TtuObtrIFdi8P4/rTW6gAgJkHOuNmusjx+ZEDAA7rHKJKsNvmjXR4ACBU6bTvEgqfNVf3y+NFoGxVwLrlKoH9dNp3lRPMkl3UTcggz7yW9mGtS1jWCGTQF4DECWbJ6Q3EskKtRqdiG1cC2aqArBEanQqEQRtmxGVAK5hPtlHPl3Wh7LbZtO9YQVEmDNqQQXPNIrzoWbMIpMkjYegRQC6AhBmxZhGeByrhkxECCP9b8AeONozYOlq1pgAAAABJRU5ErkJggg=='
    },
    {
      text: '10-99',
      image:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAYAAAAGCAYAAADgzO9IAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAnklEQVQImTXBOw6CQBQF0AvzVEL8RRNoWACdjcYQG1yEaxBrO3Zga8cG3IS1iYmhtyJSmtgMoRgH5mHlOQQAVRaFluYdNMtuyJfxPv9QlUWheulrXbaBUQxv7aRVFsVkfdttXbaBfGoAgHC0723shNCgZxTjzyhGp7ig0fFxlqdV2p80vpYGs8XgLVyREwBYU4rnSzvpFBfCFbl7uN9+boJAlbqAmzIAAAAASUVORK5CYII='
    },
    {
      text: '0-9',
      image:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAQUlEQVQImUXJsRGAMAhA0W+OYahhp2Qm53AOuJRs4BScFha+9gnANp0N4Vkh23Q+HOf4YklDDH7iWbFNV8PtWdcLp2MVQHUJk/8AAAAASUVORK5CYII='
    }
  ]
}

// Descriptions follow the summaries of the Esri Living Atlas items
const modisInfo: LayerInfo = {
  description: {
    'en-US':
      "Thermal activity detected worldwide by the MODIS sensors on NASA's Aqua and Terra satellites, published by Esri Living Atlas.",
    'es-ES':
      'Actividad térmica detectada en todo el mundo por los sensores MODIS de los satélites Aqua y Terra de la NASA, publicada por Esri Living Atlas.',
    'pt-BR':
      'Atividade térmica detectada em todo o mundo pelos sensores MODIS dos satélites Aqua e Terra da NASA, publicada pelo Esri Living Atlas.',
    'fr-FR':
      'Activité thermique détectée dans le monde entier par les capteurs MODIS des satellites Aqua et Terra de la NASA, publiée par Esri Living Atlas.'
  },
  credit: LAYER_ATTRIBUTIONS.esriLivingAtlas,
  sourceUrl: `https://www.arcgis.com/home/item.html?id=${MODIS_ITEM_ID}`
}

const viirsInfo: LayerInfo = {
  description: {
    'en-US':
      'Thermal activity detected worldwide by the VIIRS sensors on the Suomi NPP, NOAA-20 and NOAA-21 satellites, published by Esri Living Atlas.',
    'es-ES':
      'Actividad térmica detectada en todo el mundo por los sensores VIIRS de los satélites Suomi NPP, NOAA-20 y NOAA-21, publicada por Esri Living Atlas.',
    'pt-BR':
      'Atividade térmica detectada em todo o mundo pelos sensores VIIRS dos satélites Suomi NPP, NOAA-20 e NOAA-21, publicada pelo Esri Living Atlas.',
    'fr-FR':
      'Activité thermique détectée dans le monde entier par les capteurs VIIRS des satellites Suomi NPP, NOAA-20 et NOAA-21, publiée par Esri Living Atlas.'
  },
  credit: LAYER_ATTRIBUTIONS.esriLivingAtlas,
  sourceUrl: `https://www.arcgis.com/home/item.html?id=${VIIRS_ITEM_ID}`
}

const title48Hours = {
  'en-US': '48 Hours',
  'es-ES': '48 Horas',
  'pt-BR': '48 Horas',
  'fr-FR': '48 Heures'
}

const title7Days = {
  'en-US': '7 Days',
  'es-ES': '7 Días',
  'pt-BR': '7 Dias',
  'fr-FR': '7 Jours'
}

const baseLayer = {
  type: 'feature',
  zindex: 1,
  active: true,
  activeTarget: 'visibility',
  enabled: false,
  icon: 'mdi-fire'
}

export const HeatDetectionModis48 = {
  ...baseLayer,
  id: 'livingAtlas:modis_48',
  url: `${MODIS_URL}/0`,
  title: title48Hours,
  customProperties: { group: 'heatDetection:modis', info: modisInfo },
  extraArcgisParameter: { portalItem: { id: MODIS_ITEM_ID }, copyright: modisInfo.credit }
} as LayerRaw

export const HeatDetectionModis7 = {
  ...baseLayer,
  id: 'livingAtlas:modis_7',
  url: `${MODIS_URL}/1`,
  title: title7Days,
  customProperties: { group: 'heatDetection:modis', info: modisInfo },
  extraArcgisParameter: { portalItem: { id: MODIS_ITEM_ID }, layerId: 1, copyright: modisInfo.credit }
} as LayerRaw

// The VIIRS service has a single layer, split into the two time windows by `hours_old`.
export const HeatDetectionViirs48 = {
  ...baseLayer,
  id: 'livingAtlas:viirs_48',
  url: VIIRS_URL,
  where: 'hours_old < 49',
  title: title48Hours,
  customProperties: { group: 'heatDetection:viirs', info: viirsInfo },
  legend: viirsLegend,
  extraArcgisParameter: { portalItem: { id: VIIRS_ITEM_ID }, copyright: viirsInfo.credit }
} as LayerRaw

export const HeatDetectionViirs7 = {
  ...baseLayer,
  id: 'livingAtlas:viirs_7',
  url: VIIRS_URL,
  where: 'hours_old < 169',
  title: title7Days,
  customProperties: { group: 'heatDetection:viirs', info: viirsInfo },
  legend: viirsLegend,
  extraArcgisParameter: { portalItem: { id: VIIRS_ITEM_ID }, copyright: viirsInfo.credit }
} as LayerRaw

export const heatDetectionLayerIds = [HeatDetectionModis48.id, HeatDetectionModis7.id, HeatDetectionViirs48.id, HeatDetectionViirs7.id]

// Area the heat detection queries are restricted to, in Web Mercator (the map view's
// spatial reference, which is also the one of the tile extents in the queries).
let filterArea: Polygon | undefined

// Restricts the heat detection layers to the given area on the server side: the feature
// services only return hotspots that intersect it.
export function setHeatDetectionFilterArea(area: EsriPolygon | undefined) {
  filterArea = area && (webMercatorUtils.geographicToWebMercator(Polygon.fromJSON(area)) as Polygon)
}

// Tile extent of an envelope query, or undefined when its format or spatial reference is not
// the expected one: comma separated `xmin,ymin,xmax,ymax` or Esri JSON, in the filter area's
// spatial reference.
function tileExtent(query: Record<string, unknown>, spatialReference: SpatialReference): Extent | undefined {
  try {
    const geometry = String(query.geometry)
    const [xmin, ymin, xmax, ymax] = geometry.split(',').map(Number)
    const json = geometry.startsWith('{') ? JSON.parse(geometry) : { xmin, ymin, xmax, ymax }
    const inSR = query.inSR ?? query.defaultSR
    const tileSR = json.spatialReference ?? (typeof inSR === 'string' && inSR.startsWith('{') ? JSON.parse(inSR) : { wkid: Number(inSR) })
    const valid = [json.xmin, json.ymin, json.xmax, json.ymax].every(Number.isFinite)
    if (!valid || !SpatialReference.fromJSON(tileSR).equals(spatialReference)) return undefined
    return Extent.fromJSON({ ...json, spatialReference: spatialReference.toJSON() })
  } catch {
    return undefined
  }
}

// Restricts a heat detection query to the filter area, if there is one.
// With many hotspots the layers query them tile by tile, with the tile extent as `geometry`.
// Replacing it with the whole area would make every tile download all the hotspots of the
// area, so each tile queries the part of the area it covers instead. Queries without a
// geometry (counts, or the whole dataset when it is small) get the whole area, and so do
// tiles in an unexpected format: still the right hotspots, only more data.
export function restrictHeatDetectionQuery(query: Record<string, unknown>) {
  if (!filterArea) return
  let area: Polygon | null = filterArea
  if (query.geometryType === 'esriGeometryEnvelope') {
    const tile = tileExtent(query, filterArea.spatialReference)
    if (tile) area = clipOperator.execute(filterArea, tile) as Polygon | null
  } else if (query.geometry) {
    // Other spatial queries are left as they are
    return
  }
  if (!area?.rings.length) {
    query.where = '1=0'
    return
  }
  query.geometry = JSON.stringify(area.toJSON())
  query.geometryType = 'esriGeometryPolygon'
  query.inSR = area.spatialReference.wkid
}

// A single interceptor for both services: ArcGIS only applies the first one matching a URL.
esriConfig.request.interceptors?.push({
  urls: [VIIRS_URL, MODIS_URL],
  before: ({ url, requestOptions }) => {
    if (url.endsWith('/query') && requestOptions?.query) restrictHeatDetectionQuery(requestOptions.query)
  },
  // The VIIRS service declares a timeInfo (24 h steps on acq_time), which makes Karonte treat
  // the layer as time-enabled and impose a one-step time extent on its queries: they come back
  // empty and the time slider shows up. The time windows here come from `hours_old` instead, so
  // hide the timeInfo from the service metadata.
  after: (response) => {
    if (response.url?.startsWith(VIIRS_URL) && response.data?.timeInfo) delete response.data.timeInfo
  }
})
